import clsx from 'clsx';

/**
 * Formatting for an operations tool. Denser than the storefront's on purpose —
 * dates are short, money has no decimals unless it needs them, and every numeric
 * column is tabular so a sorted list does not jitter.
 */

export const cn = (...parts) => clsx(...parts);

const rupees = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** Money is integer paise from the API and becomes rupees only when read. */
export const formatMoney = (paise, { symbol = true } = {}) => {
  const value = (paise ?? 0) / 100;
  const formatted = rupees.format(value);
  return symbol ? `₹${formatted}` : formatted;
};

/** ₹1.2L / ₹34.5k — for chart axes and stat tiles where the exact rupee is noise. */
export const formatCompactMoney = (paise) => {
  const value = (paise ?? 0) / 100;
  if (value >= 10_000_000) return `₹${(value / 10_000_000).toFixed(1)}Cr`;
  if (value >= 100_000) return `₹${(value / 100_000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}k`;
  return `₹${rupees.format(value)}`;
};

const short = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' });
const full = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const withTime = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
});

export const formatDate = (value) => (value ? full.format(new Date(value)) : '—');
export const formatShortDate = (value) => (value ? short.format(new Date(value)) : '—');
export const formatDateTime = (value) => (value ? withTime.format(new Date(value)) : '—');

/** "4m ago" is what an operator watching a queue actually wants to read. */
export const formatAgo = (value) => {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diff / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
};

export const pluralise = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

export const truncate = (text, length = 60) => {
  const value = String(text ?? '');
  return value.length > length ? `${value.slice(0, length - 1).trimEnd()}…` : value;
};

export const plainText = (html, length) => {
  const text = String(html ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return length ? truncate(text, length) : text;
};

/** Rupee input in the admin, integer paise on the wire. */
export const rupeesToPaise = (value) => Math.round(Number(value || 0) * 100);
export const paiseToRupees = (value) => (value ?? 0) / 100;
